// List of motions filed on a matter. Reads matter.workspace.motions[].
// Status chip color-codes pending / granted / denied / filed.

import { pick, type WidgetProps } from "./types";
import { WidgetCard } from "./WidgetCard";

interface MotionShape {
  title: string;
  status?: "pending" | "granted" | "denied" | "filed";
  filedDate?: string;
}

const STATUS_COLOR: Record<string, { bg: string; fg: string }> = {
  pending: { bg: "var(--warn-50)", fg: "var(--warn-600)" },
  granted: { bg: "var(--ok-50)", fg: "var(--ok-600)" },
  denied: { bg: "var(--red-50)", fg: "var(--red-600)" },
  filed: { bg: "var(--bg-2)", fg: "var(--muted)" },
};

export function MotionListWidget({ config, matter }: WidgetProps) {
  const motions = pick<MotionShape[]>(matter.workspace, "motions") ?? [];
  const title = config.title ?? "Motions";

  if (motions.length === 0) {
    return (
      <WidgetCard title={title}>
        <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
          No motions filed on this matter.
        </div>
      </WidgetCard>
    );
  }

  return (
    <WidgetCard title={title} sub={`${motions.length}`}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {motions.map((m, i) => {
          const c = m.status
            ? (STATUS_COLOR[m.status] ?? STATUS_COLOR.filed!)
            : STATUS_COLOR.filed!;
          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "6px 0",
                borderBottom: i === motions.length - 1 ? "none" : "1px solid var(--line-2)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink)" }}>
                  {m.title}
                </div>
                {m.filedDate && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    Filed {m.filedDate}
                  </div>
                )}
              </div>
              {m.status && (
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    padding: "2px 8px",
                    borderRadius: 4,
                    background: c.bg,
                    color: c.fg,
                    marginLeft: 8,
                    whiteSpace: "nowrap",
                  }}
                >
                  {m.status}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </WidgetCard>
  );
}
